import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { calendarService } from '../../../services/calendarService';
import { analyticsService } from '../../../services/analyticsService';
import { format, addDays, startOfDay, endOfDay, parseISO, getDay, getHours, getMinutes } from 'date-fns';
import { trackConsultationBooking, trackBookingStart, trackBookingConfirmation } from '../../../utils/analyticsEvents';
import { useNavigate } from 'react-router-dom';

const ConsultationBookingForm = () => {
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    company: '',
    serviceInterest: '',
    title: '',
    description: '',
    selectedSlot: null,
    durationMinutes: 60
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState('');
  const [isGeneratingSlots, setIsGeneratingSlots] = useState(false);
  const [sessionId] = useState(() => {
    // Get or create session ID for tracking
    let sid = sessionStorage.getItem('conversion_session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
      sessionStorage.setItem('conversion_session_id', sid);
    }
    return sid;
  });
  const [bookingStartTracked, setBookingStartTracked] = useState(false);
  const navigate = useNavigate();

  const serviceOptions = [
    'Data Strategy Consultation',
    'Fractional CDO Services',
    'AI Solutions',
    'Data Maturity Assessment',
    'Analytics Implementation',
    'Data Governance',
    'Other'
  ];

  // Allowed time slots: Mondays at 10:00 AM, 11:30 AM, 2:00 PM, 3:30 PM
  const ALLOWED_TIMES = [
    { hours: 10, minutes: 0, label: '10:00 AM - 11:00 AM' },  // 10:00 AM
    { hours: 11, minutes: 30, label: '11:30 AM - 12:30 PM' }, // 11:30 AM
    { hours: 14, minutes: 0, label: '2:00 PM - 3:00 PM' },    // 2:00 PM
    { hours: 15, minutes: 30, label: '3:30 PM - 4:30 PM' }    // 3:30 PM
  ];

  const isAllowedSlot = (slotStartTime) => {
    try {
      const date = parseISO(slotStartTime);
      const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
      const hours = getHours(date);
      const minutes = getMinutes(date);

      // Check if it's Monday (day 1)
      if (dayOfWeek !== 1) {
        return false;
      }

      // Check if time matches one of the allowed slots
      return ALLOWED_TIMES?.some(
        allowedTime => allowedTime?.hours === hours && allowedTime?.minutes === minutes
      );
    } catch {
      return false;
    }
  };

  useEffect(() => {
    loadAvailableSlots();
  }, []);

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      const startDate = startOfDay(new Date())?.toISOString();
      const endDate = endOfDay(addDays(new Date(), 28))?.toISOString(); // 4 weeks = 28 days
      
      // Get available slots
      const slots = await calendarService?.getAvailableSlots(startDate, endDate);
      
      // Get booked slots
      const booked = await calendarService?.getBookedSlots(startDate, endDate);
      
      // Filter slots to only show Mondays at allowed times
      const filteredSlots = slots?.filter(slot => isAllowedSlot(slot?.startTime)) || [];
      
      console.log('Total slots fetched:', slots?.length);
      console.log('Filtered Monday slots (available for booking):', filteredSlots?.length);
      console.log('Booked slots:', booked?.length);
      
      // If no Monday slots available, attempt to generate them automatically
      if (filteredSlots?.length === 0) {
        console.log('No available Monday slots found, attempting auto-generation...');
        const generated = await autoGenerateSlots();
        if (generated) {
          return; // loadAvailableSlots will be called again after generation
        }
      }
      
      setAvailableSlots(filteredSlots);
      setBookedSlots(booked);
      setError('');
    } catch (err) {
      console.error('Error loading slots:', err);
      setError('');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const autoGenerateSlots = async () => {
    try {
      setIsGeneratingSlots(true);
      console.log('Auto-generating Monday consultation slots...');
      
      // Call the service to generate Monday slots
      await calendarService?.generateMondaySlots();
      
      console.log('Slots generated successfully, reloading...');
      
      // Reload slots after generation
      const startDate = startOfDay(new Date())?.toISOString();
      const endDate = endOfDay(addDays(new Date(), 28))?.toISOString();
      const slots = await calendarService?.getAvailableSlots(startDate, endDate);
      const filteredSlots = slots?.filter(slot => isAllowedSlot(slot?.startTime)) || [];
      
      setAvailableSlots(filteredSlots);
      setError('');
      return true;
    } catch (err) {
      console.error('Auto-generation failed (slots will be shown as unavailable):', err);
      // Silently fail — slots will appear as empty, no error shown to user
      setAvailableSlots([]);
      setError('');
      return false;
    } finally {
      setIsGeneratingSlots(false);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Track booking start when user begins filling form (first meaningful input)
    if (!bookingStartTracked && value && ['clientName', 'clientEmail', 'serviceInterest']?.includes(name)) {
      setBookingStartTracked(true);
      
      // Get source from session storage (set by assessment pages)
      const bookingSource = sessionStorage.getItem('booking_source') || 'direct';
      
      // Track in Google Analytics
      trackBookingStart(formData?.serviceInterest || 'Unknown', bookingSource);
      
      // Track in Supabase
      analyticsService?.trackConversionEvent({
        sessionId,
        eventType: 'booking_start',
        eventCategory: 'Conversion',
        eventData: {
          serviceInterest: formData?.serviceInterest,
          source: bookingSource
        }
      })?.catch(err => console.error('Error tracking booking start:', err));

      // Update conversion funnel status
      analyticsService?.upsertConversionFunnel({
        sessionId,
        conversionStatus: 'booking_started',
        serviceInterest: formData?.serviceInterest
      })?.catch(err => console.error('Error updating funnel:', err));
    }
  };

  const handleSlotSelect = (slot) => {
    setFormData(prev => ({
      ...prev,
      selectedSlot: slot
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const bookingData = {
        clientName: formData?.clientName,
        clientEmail: formData?.clientEmail,
        clientPhone: formData?.clientPhone || null,
        company: formData?.company || null,
        serviceInterest: formData?.serviceInterest,
        title: formData?.title || `${formData?.serviceInterest} Consultation`,
        description: formData?.description || null,
        slotId: formData?.selectedSlot?.id,
        scheduledStart: formData?.selectedSlot?.startTime,
        scheduledEnd: formData?.selectedSlot?.endTime,
        durationMinutes: formData?.durationMinutes
      };

      const booking = await calendarService?.createBooking(bookingData);
      
      // Get booking source and assessment data from session
      const bookingSource = sessionStorage.getItem('booking_source') || 'direct';
      const assessmentData = sessionStorage.getItem('assessment_data');
      let assessmentInfo = null;
      
      if (assessmentData) {
        try {
          assessmentInfo = JSON.parse(assessmentData);
        } catch (e) {
          console.error('Error parsing assessment data:', e);
        }
      }

      // Track consultation booking conversion (existing)
      trackConsultationBooking(
        formData?.serviceInterest,
        formData?.selectedSlot?.startTime
      );

      // Track booking confirmation in Google Analytics
      trackBookingConfirmation(
        formData?.serviceInterest,
        booking?.id,
        bookingSource
      );

      // Track booking confirmation event in Supabase
      await analyticsService?.trackConversionEvent({
        sessionId,
        eventType: 'booking_complete',
        eventCategory: 'Conversion',
        eventData: {
          bookingId: booking?.id,
          serviceInterest: formData?.serviceInterest,
          source: bookingSource,
          scheduledStart: formData?.selectedSlot?.startTime
        }
      });

      // Calculate time to booking if assessment data exists
      let timeToBookingMinutes = null;
      if (assessmentInfo?.completedAt) {
        const assessmentTime = new Date(assessmentInfo?.completedAt);
        const bookingTime = new Date();
        timeToBookingMinutes = Math.round((bookingTime - assessmentTime) / (1000 * 60));
      }

      // Update conversion funnel with booking completion
      await analyticsService?.upsertConversionFunnel({
        sessionId,
        bookingId: booking?.id,
        serviceInterest: formData?.serviceInterest,
        conversionStatus: 'booking_completed',
        timeToBookingMinutes
      });

      // If assessment data exists, track assessment-to-booking conversion
      if (assessmentInfo) {
        const { trackAssessmentToBooking, trackLeadQuality } = await import('../../../utils/analyticsEvents');
        
        trackAssessmentToBooking(
          assessmentInfo?.assessmentType,
          assessmentInfo?.score,
          assessmentInfo?.maturityLevel,
          formData?.serviceInterest,
          timeToBookingMinutes
        );

        // Get lead quality score from funnel metrics
        try {
          const funnelData = await analyticsService?.getConversionFunnelAnalytics('all');
          const currentFunnel = funnelData?.funnelData?.find(f => f?.sessionId === sessionId);
          
          if (currentFunnel?.leadQualityScore) {
            trackLeadQuality(
              currentFunnel?.leadQualityScore,
              assessmentInfo?.assessmentType,
              formData?.serviceInterest
            );
          }
        } catch (err) {
          console.error('Error tracking lead quality:', err);
        }
      }

      // Clear session data after successful booking
      sessionStorage.removeItem('booking_source');
      sessionStorage.removeItem('assessment_data');
      
      // Store booking data for confirmation page
      const bookingConfirmationData = {
        ...booking,
        source: bookingSource
      };
      sessionStorage.setItem('booking_confirmation', JSON.stringify(bookingConfirmationData));
      
      // Redirect to confirmation page
      navigate('/post-booking-confirmation-preparation-center-consultation-success-hub', {
        state: { bookingData: bookingConfirmationData }
      });
    } catch (err) {
      setError(err?.message || 'Failed to book consultation');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatSlotTime = (slot) => {
    try {
      const start = parseISO(slot?.startTime);
      const end = parseISO(slot?.endTime);
      return `${format(start, 'EEE, MMM d, yyyy')} at ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
    } catch {
      return 'Invalid date';
    }
  };

  // Group slots by week for better organization
  const groupSlotsByWeek = (slots) => {
    const weeks = {};
    slots?.forEach(slot => {
      try {
        const date = parseISO(slot?.startTime);
        const weekStart = format(date, 'MMM d, yyyy');
        if (!weeks?.[weekStart]) {
          weeks[weekStart] = [];
        }
        weeks?.[weekStart]?.push(slot);
      } catch (e) {
        console.error('Error parsing slot date:', e);
      }
    });
    return weeks;
  };

  const weeklySlots = groupSlotsByWeek(availableSlots);

  if (loading || isGeneratingSlots) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-2xl shadow-elevation p-8 text-center">
          <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Icon name="Calendar" size={32} className="text-secondary" />
          </div>
          <p className="text-muted-foreground">
            {isGeneratingSlots ? 'Setting up consultation slots...' : 'Loading available times...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-2xl shadow-elevation p-8 text-center transition-all duration-500 hover:shadow-2xl">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 hover:bg-destructive/20 hover:scale-110">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h3 className="text-xl font-semibold text-primary mb-2">Unable to Load Slots</h3>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="space-y-3">
            <Button
              onClick={loadAvailableSlots}
              iconName="RefreshCw"
              iconPosition="left"
              className="transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            >
              Try Again
            </Button>
            <p className="text-sm text-muted-foreground">
              Or contact us directly at{' '}
              <a href="mailto:mark@northstaranalytics.eu" className="text-secondary hover:underline transition-colors duration-300 hover:text-secondary/80">
                mark@northstaranalytics.eu
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitStatus === 'success') {
    return (
      <section className="py-20 bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl shadow-elevation-2 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Consultation Booked Successfully!
            </h3>
            <p className="text-muted-foreground mb-6">
              We have received your booking request. You will receive a confirmation email shortly with meeting details and a calendar invite.
            </p>
            <div className="bg-surface rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-foreground mb-4">What happens next:</h4>
              <div className="space-y-3 text-left">
                <div className="flex items-start space-x-3">
                  <Icon name="Mail" size={20} className="text-secondary mt-1" />
                  <div>
                    <p className="text-sm font-medium text-primary uppercase tracking-wide">Confirmation Email</p>
                    <p className="text-sm text-muted-foreground">Check your inbox for booking confirmation</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Calendar" size={20} className="text-secondary mt-1" />
                  <div>
                    <p className="text-sm font-medium text-primary uppercase tracking-wide">Calendar Invite</p>
                    <p className="text-sm text-muted-foreground">Add the meeting to your calendar</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Video" size={20} className="text-secondary mt-1" />
                  <div>
                    <p className="text-sm font-medium text-primary uppercase tracking-wide">Meeting Link</p>
                    <p className="text-sm text-muted-foreground">Video conference details will be included</p>
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setSubmitStatus(null)}
            >
              Book Another Consultation
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white border-b border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-secondary" />
            </div>
            <span className="text-sm font-semibold text-secondary uppercase tracking-wider">Strategic Consultation</span>
          </div>
        </div>
        
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Book Your Strategic Consultation
          </h2>
          
          {/* Display Selected Time Slot in Header */}
          {formData?.selectedSlot && (
            <div className="max-w-2xl mx-auto mb-4 bg-primary/10 border-2 border-primary rounded-lg p-4">
              <div className="flex items-center justify-center gap-3">
                <Icon name="Calendar" size={24} className="text-primary" />
                <div className="text-left">
                  <p className="text-sm font-medium text-primary uppercase tracking-wide">Selected Time Slot</p>
                  <p className="text-lg font-bold text-foreground">{formatSlotTime(formData?.selectedSlot)}</p>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
Schedule a 45-minute discovery session with our data strategy experts. Available every Monday at convenient times.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2 text-primary" />
              Your Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="clientName"
                  required
                  value={formData?.clientName}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="clientEmail"
                  required
                  value={formData?.clientEmail}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="clientPhone"
                  value={formData?.clientPhone}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="+353 1 234 5678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData?.company}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Your Company"
                />
              </div>
            </div>
          </div>

          {/* Merged Section: Consultation Details & Time Slot Selection */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Briefcase" size={20} className="mr-2 text-primary" />
              Consultation Details & Scheduling
            </h3>
            <div className="space-y-6">
              {/* Service Interest */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Service Interest <span className="text-red-500">*</span>
                </label>
                <select
                  name="serviceInterest"
                  required
                  value={formData?.serviceInterest}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select a service</option>
                  {serviceOptions?.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
              
              {/* Additional Details */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Additional Details
                </label>
                <textarea
                  name="description"
                  value={formData?.description}
                  onChange={(e) => handleInputChange(e)}
                  rows={4}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Tell us about your specific needs or questions..."
                />
              </div>

              {/* Time Slot Selection */}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center transition-colors duration-300 hover:text-secondary">
                  <Icon name="Calendar" size={20} className="mr-2 transition-transform duration-300 hover:scale-110" />
                  Select Your Preferred Time
                </h3>
                
                {availableSlots?.length === 0 ? (
                  <div className="bg-surface rounded-lg p-6 text-center transition-all duration-300 hover:bg-surface-hover">
                    <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No available slots at the moment</p>
                    <p className="text-sm text-muted-foreground">Please contact us directly to schedule</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(weeklySlots)?.map(([week, slots]) => (
                      <div key={week}>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3 transition-colors duration-300 hover:text-primary">
                          Week of {week}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {slots?.map((slot) => {
                            const isBooked = bookedSlots?.some(booked => booked?.slotId === slot?.id);
                            const isSelected = formData?.selectedSlot?.id === slot?.id;
                            
                            return (
                              <button
                                key={slot?.id}
                                type="button"
                                disabled={isBooked}
                                onClick={() => handleSlotSelect(slot)}
                                className={`p-4 rounded-lg border-2 text-left transition-all duration-300 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                                  isBooked
                                    ? 'border-border bg-surface opacity-50 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-secondary bg-secondary/5 shadow-md scale-105'
                                    : 'border-border hover:border-secondary hover:bg-secondary/5 hover:shadow-md hover:scale-105 active:scale-95 cursor-pointer'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                                      isBooked
                                        ? 'bg-surface'
                                        : isSelected
                                        ? 'bg-secondary text-white scale-110' :'bg-surface group-hover:bg-secondary/10'
                                    }`}>
                                      <Icon 
                                        name={isBooked ? 'X' : isSelected ? 'CheckCircle' : 'Clock'} 
                                        size={20} 
                                        className={isBooked ? 'text-muted-foreground' : isSelected ? 'text-white' : 'text-secondary'}
                                      />
                                    </div>
                                    <div>
                                      <p className={`font-medium transition-colors duration-300 ${
                                        isBooked ? 'text-muted-foreground' : 'text-primary'
                                      }`}>
                                        {format(parseISO(slot?.startTime), 'EEEE, MMM d')}
                                      </p>
                                      <p className={`text-sm transition-colors duration-300 ${
                                        isBooked ? 'text-muted-foreground' : 'text-muted-foreground'
                                      }`}>
                                        {format(parseISO(slot?.startTime), 'h:mm a')} - {format(parseISO(slot?.endTime), 'h:mm a')}
                                      </p>
                                    </div>
                                  </div>
                                  {isBooked && (
                                    <span className="text-xs font-medium text-muted-foreground bg-surface px-2 py-1 rounded">
                                      Booked
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Selected Slot Summary */}
          {formData?.selectedSlot && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="CheckCircle" size={18} className="text-primary mr-2" />
                Selected Time Slot
              </h4>
              <p className="text-foreground">{formatSlotTime(formData?.selectedSlot)}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={isSubmitting || !formData?.selectedSlot}
              className="px-8 min-h-[44px] transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader2" size={18} className="animate-spin mr-2" />
                  Booking...
                </>
              ) : (
                <>
                  <Icon name="Calendar" size={18} className="mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ConsultationBookingForm;